import { resetIndexedDB, saveImageToDB } from "../../../utils/indexedDB";
import framesMap from "../../../config/frames-map.json";

// Bridges a DesignerResult (SDK save) into the legacy save contract that
// product-select → checkout → complete-checkout consume today (saveCanvas.js):
//   - a label blob in IndexedDB 'resultImages' under a fresh resultImageId
//   - localStorage: resultImageId, petName, label_template, imageMetadata
//   - "resultImageReady" event
// plus the SDK-specific fields (asset ids, variant, draft) returned for the cart line.
//
// The SDK's previewUrl is the display composite (product mockup + label) and is an
// ephemeral object URL. The 900×1200 print artifact is NOT exposed locally by design
// (uploaded as renderedAssetId; pk keys cannot read it back) — so we recover the label
// image by cropping the composite to its labelZone rect. Display-resolution only: fine
// for cart thumbnails and the checkout mockup composite; fulfillment artwork comes from
// renderedAssetId server-side (Phase 3).

const cutoutToFrameName = Object.fromEntries(
  Object.entries(framesMap.byName).map(([name, v]) => [v.cutoutLabelId, name]),
);

async function cropLabelFromPreview(previewUrl, labelZone) {
  const img = new Image();
  img.src = previewUrl;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () => reject(new Error("preview image failed to load"));
  });
  const sx = labelZone.x * img.naturalWidth;
  const sy = labelZone.y * img.naturalHeight;
  const sw = labelZone.width * img.naturalWidth;
  const sh = labelZone.height * img.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(sw));
  canvas.height = Math.max(1, Math.round(sh));
  canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("label crop failed"))), "image/png"),
  );
}

export async function persistDesignerResult(result, cartItems) {
  const labelBlob = await cropLabelFromPreview(result.previewUrl, result.labelZone);
  // The full display composite (product mockup + label in its real zone) becomes the
  // cart image — the SDK already rendered it, fully local via the blob URL.
  const displayBlob = await fetch(result.previewUrl).then((r) => r.blob()).catch(() => null);
  URL.revokeObjectURL(result.previewUrl);

  const imageId = Date.now().toString();
  localStorage.setItem("resultImageId", imageId);
  resetIndexedDB("resultImages", cartItems);
  resetIndexedDB("onProductImages", cartItems);
  await saveImageToDB("resultImages", imageId, labelBlob, null, null, []);
  window.dispatchEvent(new CustomEvent("resultImageReady"));

  if (result.personalizationText) {
    localStorage.setItem("petName", result.personalizationText);
  } else {
    localStorage.removeItem("petName");
  }

  // label_template keeps the store's frame naming for complete-checkout/order_items;
  // unknown cutouts (e.g. new API-only frames) fall back to the cutout label id.
  localStorage.setItem(
    "label_template",
    cutoutToFrameName[result.cutoutLabelId] || result.cutoutLabelId,
  );

  // Transform is in 900×1200 print px — same space as the legacy imageMetadata
  // (which additionally carried width/height/maxScale; consumers tolerate their absence).
  localStorage.setItem("imageMetadata", JSON.stringify(result.transform));

  const design = {
    draftId: result.draftId,
    sku: result.sku,
    variantId: result.variantId ?? null,
    cutoutLabelId: result.cutoutLabelId,
    sourceAssetId: result.artwork.sourceAssetId,
    renderedAssetId: result.artwork.renderedAssetId,
    personalizationText: result.personalizationText ?? null,
    lowRes: result.lowRes,
    resultImageId: imageId,
  };
  // displayBlob is returned separately — blobs must never enter redux-persist.
  return { design, displayBlob };
}
